import {
  Button,
  Divider,
  Drawer,
  Form,
  Select,
  Tree,
  TreeNodeProps,
  useSelect,
} from '@pankod/refine-antd'
import {
  useCustomMutation,
  useGetIdentity,
  useNotification,
} from '@pankod/refine-core'
import React, { useCallback, useState } from 'react'

import { PopDeleteConfirm } from '@components/PopDeleteConfirm'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { TCommonError } from 'src/interfaces/common'
import {
  CONFIGURATION_GROUP_KEY,
  ConfigurationTreeKey,
  ConfigurationType,
  ProgramImportConfigurationRequest,
} from './types'
import {
  buildProgramImportConfigurationRequest,
  getConfigurationTreeState,
  getSourceProgramOptions,
  updateConfigurationSelection,
} from './utils'
import HistoricalComponent from '@resources/lms-management/program/program-setup/blocks/program-import-config/HistoricalComponent'

import type { TLMSProgram, TProgramLastConfig } from '../../../types'

const CONFIGURATION_TREE: TreeNodeProps[] = [
  {
    title: 'Registrasi Blueprint',
    key: 'PROGRAM_CONFIGURATION_BLUEPRINT_REGISTRATION',
  },
  {
    title: 'Manajemen Peserta',
    key: CONFIGURATION_GROUP_KEY.PARTICIPANT_MANAGEMENT,
    children: [
      {
        title: 'Aktifkan auto approve peserta diklat',
        key: 'PROGRAM_CONFIGURATION_AUTO_APPROVE_DIKLAT_PARTICIPANTS',
      },
      {
        title: 'Aktifkan hide tambah peserta',
        key: 'PROGRAM_CONFIGURATION_HIDE_ADD_PARTICIPANT',
      },
      {
        title: 'Aktifkan ubah profil (primer)',
        key: 'PROGRAM_CONFIGURATION_MODIFY_PROFILE',
        children: [
          {
            title: 'Auto-approve pengajuan',
            key: 'PROGRAM_CONFIGURATION_AUTO_APPROVE_MODIFY_PROFILE',
          },
        ],
      },
    ],
  },
  {
    title: 'Pengaturan Nilai',
    key: 'PROGRAM_CONFIGURATION_GRADE_SETTINGS',
  },
  {
    title: 'Pendaftaran Mandiri',
    key: 'PROGRAM_CONFIGURATION_SELF_ENROLLMENT',
  },
  {
    title: 'Umpan Balik Program',
    key: 'PROGRAM_CONFIGURATION_UMPAN_BALIK',
  },
  {
    title: 'Sertifikat',
    key: CONFIGURATION_GROUP_KEY.CERTIFICATE,
    children: [
      {
        title: 'Design Template sertifikat',
        key: 'PROGRAM_CONFIGURATION_CERTIFICATE_DESIGN_TEMPLATES',
      },
      {
        title: 'Konfigurasi prasyarat sertifikat',
        key: 'PROGRAM_CONFIGURATION_CERTIFICATE_PREREQUISITES',
      },
    ],
  },
]

const PROGRAM_RESOURCE = 'programs'
const PROGRAM_ENTITIES_RESOURCE = 'entities/programs/admin-entitas'

type ProgramImportConfigDrawerState = {
  visible: boolean
  currentProgramId: string | number
  currentProgramName: string
}

export const useProgramImportConfigDrawer = () => {
  const [state, setState] = useState<ProgramImportConfigDrawerState>({
    visible: false,
    currentProgramId: '',
    currentProgramName: '',
  })
  const onClose = useCallback(() => {
    setState((currentState) => ({ ...currentState, visible: false }))
  }, [])

  return {
    show: (currentProgramId: number | string, currentProgramName: string) => {
      setState({ visible: true, currentProgramId, currentProgramName })
    },
    ...state,
    onClose,
  }
}

type ProgramImportConfigDrawerProps = ProgramImportConfigDrawerState & {
  entityId?: string
  onClose: () => void
  programLastConfig?: TProgramLastConfig | null
  refetchProgramDetail: () => Promise<unknown>
}

type ProgramImportConfigForm = {
  sourceProgramId?: string | number
  listConfigurationType?: ConfigurationType[]
}

export const ProgramImportConfigDrawer = ({
  currentProgramId,
  currentProgramName,
  entityId,
  onClose,
  programLastConfig,
  refetchProgramDetail,
  visible,
}: ProgramImportConfigDrawerProps) => {
  const [form] = Form.useForm<ProgramImportConfigForm>()
  const { data: identity, isSuccess: isIdentityResolved } = useGetIdentity()
  const { open: openNotification } = useNotification()
  const [isRefreshingHistory, setIsRefreshingHistory] = useState(false)
  const isAdminEntitas = identity?.user?.LMSRole === 'admin-entitas'
  const { selectProps, queryResult } = useSelect<TLMSProgram>({
    resource: isAdminEntitas ? PROGRAM_ENTITIES_RESOURCE : PROGRAM_RESOURCE,
    dataProviderName: 'lms',
    optionLabel: 'name',
    optionValue: 'id',
    filters: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
      ...(!isAdminEntitas
        ? [
            {
              field: 'entityId',
              operator: 'eq' as const,
              value: entityId,
            },
          ]
        : []),
    ],
    queryOptions: {
      enabled:
        visible && isIdentityResolved && (isAdminEntitas || Boolean(entityId)),
    },
    defaultValueQueryOptions: {
      enabled: false,
    },
  })
  const { mutateAsync, isLoading } = useCustomMutation<
    {},
    TCommonError,
    ProgramImportConfigurationRequest
  >()
  const sourceProgramId = Form.useWatch('sourceProgramId', form)
  const selectedConfigurationTypes =
    Form.useWatch('listConfigurationType', form) || []
  const sourceOptions = getSourceProgramOptions(
    queryResult.data?.data || [],
    currentProgramId,
  )
  const selectedSourceOption = sourceOptions.find(
    ({ value }) => String(value) === String(sourceProgramId),
  )
  const treeState = getConfigurationTreeState(selectedConfigurationTypes)
  const isBusy = isLoading || isRefreshingHistory
  const shouldDisableSubmit =
    !entityId ||
    !sourceProgramId ||
    selectedConfigurationTypes.length === 0 ||
    isBusy

  const handleConfirm = async () => {
    await form.validateFields()
    const payload = buildProgramImportConfigurationRequest(
      sourceProgramId!,
      selectedConfigurationTypes,
    )

    try {
      await mutateAsync({
        values: payload,
        method: 'post',
        url: `${process.env.NEXT_PUBLIC_LMS_API_URL}/programs/${currentProgramId}/import-configuration`,
        dataProviderName: 'lms',
        errorNotification: (error) =>
          showErrorNotification(
            error,
            'Terdapat gangguan saat mengimpor konfigurasi program',
          ),
        successNotification: {
          message: 'Impor konfigurasi program berhasil',
          type: 'success',
          description: 'Sukses',
        },
      })
    } catch {
      return
    }

    form.resetFields()
    setIsRefreshingHistory(true)

    try {
      await refetchProgramDetail()
    } catch (error: unknown) {
      openNotification?.(
        showErrorNotification(
          error,
          'Impor berhasil, tetapi riwayat impor terbaru gagal dimuat',
        ),
      )
    } finally {
      setIsRefreshingHistory(false)
    }
  }

  const handleClose = () => {
    if (!isBusy) onClose()
  }

  return (
    <Drawer
      title={
        <>
          Impor Konfigurasi Program <br />| {currentProgramName}
        </>
      }
      visible={visible}
      onClose={handleClose}
      closable={!isBusy}
      destroyOnClose
      keyboard={!isBusy}
      afterVisibleChange={(drawerVisible) => {
        if (!drawerVisible) form.resetFields()
      }}
      width="400px"
      bodyStyle={{ marginBottom: '52px' }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="sourceProgramId"
          label="Program Sumber"
          required
          rules={[{ required: true, message: 'Program sumber harus dipilih' }]}
        >
          <Select
            {...selectProps}
            allowClear
            disabled={!entityId || isBusy}
            filterOption={(input, option) => {
              const searchedText =
                `${option?.label || ''}${option?.value || ''}`
                  .trim()
                  .toLowerCase()
              return searchedText.includes(input.toLowerCase())
            }}
            loading={queryResult.isFetching}
            notFoundContent={
              entityId ? undefined : 'Program ini belum memiliki entitas'
            }
            onSearch={() => {}}
            placeholder="Cari berdasarkan ID atau judul program"
            showSearch
            options={undefined}
          >
            {sourceOptions.map((option) => (
              <Select.Option
                key={option.value}
                value={option.value}
                label={option.label}
                disabled={option.disabled}
              >
                <div className="grid grid-rows-1 grid-cols-5 gap-1">
                  <span className="font-semibold col-span-5">
                    {option.label}
                  </span>
                  <span className="text-xs text-gray-500">
                    ID: <strong>{option.value}</strong>
                  </span>
                  <span className="text-xs text-gray-500 capitalize">
                    Status: <strong>{option.status}</strong>
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="listConfigurationType"
          label="Pilih Konfigurasi"
          required
          rules={[{ required: true, message: 'Konfigurasi harus dipilih' }]}
        >
          <Tree
            checkable
            checkedKeys={treeState}
            checkStrictly
            defaultExpandAll
            disabled={isBusy}
            onCheck={(_value, { checked, node }) => {
              form.setFieldsValue({
                listConfigurationType: updateConfigurationSelection(
                  selectedConfigurationTypes,
                  node.key as ConfigurationTreeKey,
                  checked,
                ),
              })
            }}
            selectable={false}
            treeData={CONFIGURATION_TREE}
          />
        </Form.Item>
      </Form>
      <Divider />
      <HistoricalComponent
        data={programLastConfig}
        loading={isRefreshingHistory}
      />
      <div className="w-full absolute bottom-0 left-0 pb-3 px-5 bg-white border-t border-gray-200">
        <PopDeleteConfirm
          title={
            <div className="max-w-sm">
              <p>
                <strong>
                  Impor konfigurasi dari {selectedSourceOption?.label}?
                </strong>
              </p>
              <p className="mb-0">
                Konfigurasi yang saat ini tersimpan pada program{' '}
                <strong>{currentProgramName}</strong> akan{' '}
                <strong>dihapus dan digantikan</strong> dengan konfigurasi dari{' '}
                program yang dipilih.
              </p>
            </div>
          }
          okText="Impor"
          cancelText="Batal"
          disabled={shouldDisableSubmit}
          cancelButtonProps={{ disabled: isBusy }}
          okButtonProps={{ loading: isBusy }}
          onConfirm={handleConfirm}
          placement="bottom"
          className="w-full"
        >
          <Button
            className="mt-2"
            disabled={shouldDisableSubmit}
            loading={isBusy}
            type="primary"
            block
          >
            Impor Konfigurasi ke Program Ini
          </Button>
        </PopDeleteConfirm>
      </div>
    </Drawer>
  )
}

export default ProgramImportConfigDrawer
